import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Typography,
  Modal,
  Backdrop,
  Fade,
  AppBar,
  Tab,
  Tabs,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from '@material-ui/core';

import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import PropTypes from 'prop-types';
import Text from '../Typography/Text';
import TextField from '../Textfields/TextField';
import Button from '../Buttons/Button';
import { notifyError } from '../../components/Messages/Notification';
import { getSystemDate } from '../../services/ProfileService';

import fontFamily from '../../assets/css/fonts';
import color from '../../assets/css/colors';

import authSvc from '../../services/AuthService';
import { readProfilePromise } from '../../services/ProfileService';
import { listSystemNumber } from '../../services/SystemNumberService';
import { readClient, getUserAccountId } from '../../services/ClientAccountService';
import { generateAccountNo } from '../../services/CommonService';
import ImageDropZone from '../../components/Misc/ImageDropZone';
import {
  dateProtoObjectToString,
  protoTimeSpanObjectToString,
  stringToModalDate,
} from '../../services/ConvertService';
import { listCustomerType } from '../../services/CustomerTypeService';
import { SelectTaxCountry } from '../../components/AutoComplete/SelectSystemCode';

import AutoCompleteCorrespondent from '../AutoComplete/AutoCompleteCorrespondent';
import AutoCompleteBranch from '../AutoComplete/AutoCompleteBranch';
import SelectAutoCompleteRepAdvisor from '../AutoComplete/SelectAutoCompleteRepAdvisor';
import { SelectSubAccountType } from '../Dropdown/SelectAccountType';
import AutoCompleteState from '../AutoComplete/AutoCompleteState';
import SelectSystemCode from '../Dropdown/SelectSystemCode';
import SelectCommonId from '../Dropdown/SelectCommonId';
import SelectSystemCodeWithDescription from '../Dropdown/SelectSystemCodeWithDescription';

import OwnerTable from '../../pages/AccountSetup/Components/OwnerTable';
import SubAccountTable from '../../pages/AccountSetup/Components/SubAccountTable';
import CustomerTypeTable from '../../pages/AccountSetup/Components/CustomerTypeTable';
import NoteTable from '../../pages/AccountSetup/Components/NoteTable';
import AttachmentTable from '../../pages/AccountSetup/Components/AttachmentTable';
import { SelectSourceOfFunds } from '../../components/MultipleSelect/SelectSourceOfFunds';

import { listUsrAccess } from '../../services/UsrAccessService';

const useStyles = makeStyles(() => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: 550,
    width: '100%',
    margin: '0 auto',
    float: 'clear',
  },
  fadeModalFull: {
    maxWidth: 1400,
    minWidth: 1400,
    maxHeight: '90vh',
    width: '100%',
    padding: '25px 30px 30px 30px',
    margin: '0px auto',
    backgroundColor: '#ffffff',
    border: 'none',
    borderRadius: 5,
    boxSizing: 'border-box',
    outline: 'none',
    overflowY: 'auto',
  },
  modalHeader: {
    backgroundColor: 'white',
    padding: 25,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    display: 'flex',
    '& div:nth-child(1)': {
      flex: 1,
    },
    '& div:nth-child(2)': {
      flex: 'none',
      '& .MuiIconButton-root': {
        borderRadius: 5,
        padding: 10,
        '&:hover svg path': {
          fill: color.primary.main,
        },
      },
      '& svg:last-child': {
        width: 15,
        height: 15,
      },
    },
  },
  modalBody: {
    backgroundColor: 'white',
    padding: '0px 25px 25px 25px',
    fontFamily: fontFamily.headers,
    '& p': {
      fontSize: 16,
      lineHeight: '24px',
    },
  },
  modalFooter: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    backgroundColor: 'white',
    padding: '0px 25px 25px 25px',
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
    position: 'relative',
    '& button': {
      width: 'auto',
    },
  },
  closeButton: {
    '& button': {
      marginTop: '-4px !important',
    },
  },
  accordionDetails: {
    display: 'block !important',
    padding: '0px 20px 25px 20px !important',
  },
  accordion: {
    borderRadius: '5px !important',
    boxShadow: '0px 1px 5px -2px #cdcfdd !important',
    border: '1px solid #efeff0 !important',
  },
  tabBody: {
    minWidth: '100%',
    padding: '0px 6px',
  },
}));

function TabPanel({ children, value: tabValue, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={tabValue !== index}
      id={`scrollable-auto-tabpanel-${index}`}
      aria-labelledby={`scrollable-auto-tab-${index}`}
      {...other}
    >
      {tabValue === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `scrollable-auto-tab-${index}`,
    'aria-controls': `scrollable-auto-tabpanel-${index}`,
  };
}

const defaultValue = {
  correspondent: '',
  accountNo: '',
  accountName: '',
  masterAccountNo: '',
  status: '',
  activatedAt: '',
  disabledAt: '',
  broker: '',
  privacyConsent: '1',
  dayTrader: false,
  multiplier: 1,
  legalEntity: '',
  accountType: '',
  marginType: '',
  branch: '',
  rep: '',
  beneficiary: '',
  currency: '',
  largeTraderId: '',
  investmentObjectiveCode: '',
  investmentObjectiveDate: '',
  taxCountry: 'US',
  tefra: 'Exempt',
  w8w9: 'W9',
  w8w9ReceivedDate: '',
  accreditedInvestor: false,
  fdidFlag: false,
  fdid: '',
  fdidEndReason: '',
  fpslParticipant: false,
  cashSweepParticipant: false,
  mutualFunds: false,
  fixedIncome: false,
  commonId: '',
  statusReason: '',
  subType: '',
  allowLiquidInvestment: false,
  participantNo: '',
  traderId: '',
  marginDisclosure: false,
  marginAgreement: false,
  stocks: true,
  etf: false,
  option: false,
  annualIncome: '',
  liquidNetWorth: '',
  sourceOfFunds: '',
  signed: false,
  imgSignature: '',
  errorMessage: '',
  validationStatus: '',
  batchNo: '',
  tin: '',
  kycStatus: '',
  formationCountry: '',
  formationState: '',
  formationDate: '',
  serviceBureau: '',
  promoCode: '',
  securityWord: '',
  securityWordHint: '',
  taxBracket: '',
  riskTolerance: '',
  plType: '',
  onboardingPercent: '',
};

export default function ClientAccountModal({
  onClose: handleCloseModal,
  openModal,
  value,
  validation,
  importMode: isImport,
  loading,
}) {
  const [tabIndexValue, setTabIndexValue] = React.useState(0);
  const handleChangeTab = (_, newValue) => {
    setTabIndexValue(newValue);
  };
  const classes = useStyles();
  const [modalData, setModalData] = useState({
    ...defaultValue,
    correspondent: value?.correspondent ? value?.correspondent : '',
  });

  const [isEdit, setIsEdit] = React.useState(false);
  const [isAccountSet, setIsAccountSet] = React.useState(false);
  const [customerTypeList, setCustomerTypeList] = React.useState([]);
  const [subAccountList, setSubAccountList] = React.useState([]);
  const [ownerList, setOwnerList] = React.useState([]);
  const [noteList, setNoteList] = React.useState([]);
  const [expanded, setExpanded] = React.useState('account');
  const [marginTypes, setMarginTypes] = useState({});
  const [initialFile, setInitialFile] = useState({});
  const [requiredField, setRequired] = React.useState({
    activatedAt: true,
  });
  const [isPendingClient, setIsPendingClient] = React.useState(false);

  const u = authSvc.getCurrentUser();

  const getDetails = async (accountId, batchNo) => {
    try {
      const data = await readClient(accountId);
      const res = await listCustomerType(accountId);
      const profileData = await readProfilePromise();

      let modalValue = { ...data.client };

      modalValue.batchNo = batchNo;
      modalValue.currency = profileData.profile.baseCurrency;

      if (data.client.currency === '') {
        modalValue.currency = profileData.profile.baseCurrency;
      } else {
        modalValue.currency = data.client.currency;
      }

      modalValue.legalEntity = data.client.legalEntity;
      modalValue.investmentObjectiveCode = data.client.investmentObjectiveCode;

      modalValue.pendingClosureAt = protoTimeSpanObjectToString(
        data.client.pendingClosureAt
      );
      modalValue.activatedAt = protoTimeSpanObjectToString(data.client.activatedAt);
      modalValue.disabledAt = protoTimeSpanObjectToString(data.client.disabledAt);

      modalValue.webTermConditions = protoTimeSpanObjectToString(
        modalValue.webTermConditions
      );
      modalValue.disclosureStatement = protoTimeSpanObjectToString(
        modalValue.disclosureStatement
      );
      modalValue.feeSchedule = protoTimeSpanObjectToString(modalValue.feeSchedule);
      modalValue.investmentObjectiveDate = dateProtoObjectToString(
        data.client.investmentObjectiveDate
      );
      modalValue.w8w9ReceivedDate = dateProtoObjectToString(data.client.w8w9ReceivedDate);
      modalValue.formationDate = dateProtoObjectToString(data.client.formationDate);
      modalValue.lastActivityDate = dateProtoObjectToString(data.client.lastActivityDate);
      modalValue.correspondent =
        modalValue.correspondent !== '    ' ? modalValue.correspondent : '';

      modalValue.participantNo =
        modalValue.participantNo !== '    ' ? modalValue.participantNo : '';

      if (modalValue.imgSignature || modalValue.imgSignature !== '') {
        await fetch(modalValue.imgSignature)
          .then(function (res) {
            return res.arrayBuffer();
          })
          .then(function (buf) {
            const file = new File([buf], 'image_data.jpg', {
              type: 'image/*',
            });
            setInitialFile(file);
          });
      } else {
        setInitialFile({});
      }
      updateRequired(modalValue.status);
      setModalData(modalValue);
      setCustomerTypeList(res?.customerTypesList);
      setSubAccountList(data.subAccountsList);
      setOwnerList(data.ownersList);
      setNoteList(data.notesList);
    } catch (error) {
      notifyError(error.message);
    }
  };

  const handleUploadedPhotoStatus = ({ meta, file }, status) => {
    if (status === 'done') {
      const reader = new FileReader();

      reader.addEventListener(
        'load',
        function () {
          setModalData({ ...modalData, imgSignature: reader.result });
        },
        false
      );

      reader.readAsDataURL(file);
    }
  };
  const handleUploadPhoto = (files) => {
    console.log(files.map((f) => f.meta));
  };

  useEffect(() => {
    if (
      modalData.taxCountry !== 'US' &&
      modalData.taxCountry !== '' &&
      modalData.taxCountry !== undefined
    ) {
      setModalData({ ...modalData, w8w9: 'W8' });
    } else {
      setModalData({ ...modalData, w8w9: 'W9' });
    }
  }, [modalData.taxCountry]);

  useEffect(() => {
    if (modalData.marginType === 'Margin' && modalData.dayTrader) {
      setModalData({ ...modalData, multiplier: 4 });
    } else if (modalData.marginType === 'Margin' && !modalData.dayTrader) {
      setModalData({ ...modalData, multiplier: 2 });
    }
  }, [modalData.marginType, modalData.dayTrader]);

  useEffect(
    () => {
      async function init() {
        try {
          const param = {
            type: 'Multiplier',
          };

          const data = await listSystemNumber(param);
          setMarginTypes(data.systemNumbersList);

          // Get user access details
          const accessParams = {
            roleId: u.RoleId,
            requestType: 'Show Selected',
          };

          const { usrAccessesList } = await listUsrAccess(accessParams);

          const findAccess = usrAccessesList.some(
            (usrAccess) =>
              usrAccess.access === 'Account Set' && usrAccess.subAccess === 'Admin'
          );

          setIsAccountSet(findAccess);

          if (!['Pending', 'In Progress', 'For Review'].includes(value.status)) {
            setIsPendingClient(false);
          }
        } catch (error) {
          console.error(error);
          notifyError(error.message);
        }
      }
      if (openModal) {
        if (value?.action !== 'add') {
          setIsEdit(true);
        }
        getDetails(value?.accountId, value?.batchNo);

        init();
      }
    },
    // eslint-disable-next-line
    [openModal]
  );

  useEffect(() => {
    const fetchAccountNo = async () => {
      const res = await generateAccountNo({
        branch: modalData.branch,
        correspondent: modalData.correspondent,
        masterAccountNo: modalData.masterAccountNo,
        rep: modalData.rep,
      });
      if (res) {
        setModalData((prevData) => ({
          ...prevData,
          accountNo: res.account.accountNo,
        }));
      }
    };
    if (
      modalData.correspondent !== '' ||
      modalData.rep !== '' ||
      modalData.branch !== '' ||
      modalData.masterAccountNo !== ''
    ) {
      if (!modalData.accountNo && !isEdit) {
        fetchAccountNo();
      }
    }
  }, [
    modalData.correspondent,
    modalData.rep,
    modalData.branch,
    modalData.masterAccountNo,
  ]);

  const handleChange = async (e, checkboxValue) => {
    const input = e.currentTarget.name ? e.currentTarget : e.target;

    let modalDataCopy = { ...modalData };
    if (input.name === 'marginType') {
      let multiplier = marginTypes.filter(
        (multiplier) => multiplier.subType === input.value
      );
      modalDataCopy.multiplier = multiplier[0]?.rate;
    }
    if (input.name === 'status') {
      const sysDate = await getSystemDate();
      const formattedDate = new Date().toISOString().slice(0, 16);

      if (input.value === 'Pending Closure') {
        modalDataCopy.closedAt = '';
        modalDataCopy.onHoldAt = '';

        modalDataCopy.pendingClosureAt =
          value.status !== 'Pending Closure'
            ? stringToModalDate(sysDate)
            : protoTimeSpanObjectToString(value.pendingClosureAt);
        modalDataCopy.activatedAt = protoTimeSpanObjectToString(value.activatedAt);
      } else if (input.value === 'Closed') {
        modalDataCopy.pendingClosureAt = '';
        modalDataCopy.onHoldAt = '';

        modalDataCopy.closedAt =
          value.status !== 'Closed'
            ? stringToModalDate(sysDate)
            : protoTimeSpanObjectToString(value.closedAt);
        modalDataCopy.activatedAt = protoTimeSpanObjectToString(value.activatedAt);
      } else if (input.value === 'On Hold') {
        modalDataCopy.pendingClosureAt = '';
        modalDataCopy.closedAt = '';

        modalDataCopy.onHoldAt =
          value.status !== 'On Hold'
            ? formattedDate
            : protoTimeSpanObjectToString(value.onHoldAt);
        modalDataCopy.activatedAt = protoTimeSpanObjectToString(value.activatedAt);
      } else if (input.value === 'Active') {
        if (value.status !== 'Active') {
          modalDataCopy.activatedAt = formattedDate;
        }
      }

      if (!['Pending Closure', 'Closed', 'On Hold'].includes(input.value)) {
        modalDataCopy.closedAt = '';
        modalDataCopy.pendingClosureAt = '';
        modalDataCopy.onHoldAt = '';
      }
    }

    if (input.name === 'correspondent') {
      modalDataCopy.rep = '';
    }

    setModalData({
      ...modalDataCopy,
      [input.name]:
        checkboxValue === true || checkboxValue === false
          ? checkboxValue
          : ['branch', 'accountNo', 'masterAccountNo', 'traderId'].includes(input.name)
          ? input.value.toUpperCase()
          : input.value,
    });
  };

  const handleMultipleFundsChange = (selected) => {
    setModalData((prevModalData) => ({
      ...prevModalData,
      sourceOfFunds: selected.map((e) => e.value).join(','),
    }));
  };

  function updateRequired(status) {
    let act = { requiredField };
    if (status === 'Active') {
      act.activatedAt = true;
    } else {
      act.activatedAt = false;
    }
    setRequired(act);
  }

  const handleExpandAccordion = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const isEntity = () => {
    return (
      ['Limited Liability Company', 'Partnership', 'Corporation', 'Trust'].includes(
        modalData.legalEntity
      ) || /Corporation/.test(modalData.legalEntity)
    );
  };

  document.onkeyup = function (e) {
    if (e.ctrlKey && e.keyCode === 13) {
      handleClose();
      return;
    }
  };

  const handleClose = (data, isEdit) => {
    if (!data) {
      handleCloseModal();
    } else {
      handleCloseModal(data, isEdit);
    }
    setModalData(defaultValue);
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      className={classes.modal}
      open={openModal}
      onClose={() => {
        handleClose();
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={openModal}>
        <div className={classes.fadeModalFull}>
          <div className={classes.modalHeader}>
            <Text
              id="transition-modal-title"
              variant="h2"
              label={'Edit Client Account'}
            />
          </div>
          <div className={classes.modalBody}>
            <div className="grd-row">
              <AppBar
                position="static"
                className={classes.tabHeader}
                style={{
                  backgroundColor: '#ffffff',
                  boxShadow: 'none',
                  borderBottom: '1px solid #e8e8e8',
                }}
              >
                <Tabs
                  value={tabIndexValue}
                  onChange={handleChangeTab}
                  indicatorColor="primary"
                  textColor="primary"
                  scrollButtons="auto"
                  variant="fullWidth"
                  centered
                >
                  <Tab label="General" {...a11yProps(0)} />
                  <Tab label="Owner" {...a11yProps(1)} />
                  <Tab label="Customer Type" {...a11yProps(2)} />
                  <Tab label="Sub Account" {...a11yProps(3)} />
                  <Tab label="Note" {...a11yProps(4)} />
                  <Tab label="Attachment" {...a11yProps(5)} />
                </Tabs>
              </AppBar>
            </div>
            <div className="grd-row">
              <TabPanel className={classes.tabBody} value={tabIndexValue} index={0}>
                <Accordion
                  className={classes.accordion}
                  expanded={expanded === 'account'}
                  onChange={handleExpandAccordion('account')}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography className={classes.heading}>Account</Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    <div className="grd-row">
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <AutoCompleteCorrespondent
                          disabled={isEdit}
                          name="correspondent"
                          value={modalData.correspondent}
                          onChange={handleChange}
                        ></AutoCompleteCorrespondent>
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <TextField
                          fullWidth
                          disabled={isEdit}
                          name="accountNo"
                          label="Account No"
                          value={modalData.accountNo}
                          onChange={handleChange}
                          inputProps={{ maxLength: 50 }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <TextField
                          fullWidth
                          disabled={(isEdit && !isAccountSet) || isPendingClient}
                          label="Account Name"
                          name="accountName"
                          value={modalData.accountName}
                          onChange={handleChange}
                          inputProps={{ maxLength: 300 }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </div>
                      <div className="grd-cell">
                        <TextField
                          fullWidth
                          disabled={(isEdit && !isAccountSet) || isPendingClient}
                          name="masterAccountNo"
                          label="Master Account No"
                          value={modalData.masterAccountNo}
                          onChange={handleChange}
                          inputProps={{ maxLength: 50 }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </div>
                    </div>
                    <div className="grd-row">
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <AutoCompleteBranch
                          fullWidth
                          disabled={(isEdit && !isAccountSet) || isPendingClient}
                          name="branch"
                          label="Branch"
                          value={modalData.branch}
                          onChange={handleChange}
                          inputProps={{ maxLength: 50 }}
                          InputLabelProps={{ shrink: true }}
                        ></AutoCompleteBranch>
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <SelectAutoCompleteRepAdvisor
                          fullWidth
                          disabled={(isEdit && !isAccountSet) || isPendingClient}
                          name="rep"
                          label="Rep/Advisor"
                          value={modalData.rep}
                          correspondent={modalData.correspondent}
                          onChange={handleChange}
                          inputProps={{ maxLength: 150 }}
                          InputLabelProps={{ shrink: true }}
                        ></SelectAutoCompleteRepAdvisor>
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <SelectSystemCode
                          disabled={(isEdit && !isAccountSet) || isPendingClient}
                          name="broker"
                          label="Broker"
                          type="Broker"
                          value={modalData.broker}
                          onChange={handleChange}
                        ></SelectSystemCode>
                      </div>
                      <div className="grd-cell">
                        <SelectSystemCode
                          disabled={(isEdit && !isAccountSet) || isPendingClient}
                          name="legalEntity"
                          label="Legal Entity"
                          placeholder="Legal Entity"
                          type="Legal Entity"
                          value={modalData.legalEntity}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="grd-row">
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <SelectSystemCode
                          disabled={(isEdit && !isAccountSet) || isPendingClient}
                          name="privacyConsent"
                          label="Privacy Consent"
                          type="Privacy Consent"
                          value={modalData.privacyConsent}
                          onChange={handleChange}
                        ></SelectSystemCode>
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <SelectSystemCode
                          disabled={(isEdit && !isAccountSet) || isPendingClient}
                          name="accountType"
                          label="Account Type"
                          placeholder="Account Type"
                          type="Client Account Type"
                          subType="Client Site"
                          value={modalData.accountType}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <SelectSubAccountType
                          disabled={(isEdit && !isAccountSet) || isPendingClient}
                          name="subType"
                          label="Sub Account Type"
                          type="Sub Account Type"
                          value={modalData.subType}
                          onChange={handleChange}
                          selectedAccountType={modalData.accountType}
                        ></SelectSubAccountType>
                      </div>
                      <div className="grd-cell">
                        <SelectCommonId
                          disabled={(isEdit && !isAccountSet) || isPendingClient}
                          freeSolo={true}
                          name="commonId"
                          fullWidth
                          label="Common ID"
                          value={modalData.commonId}
                          onChange={handleChange}
                          InputLabelProps={{ shrink: true }}
                          filter={modalData}
                        />
                      </div>
                    </div>
                    <div className="grd-row">
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <TextField
                          disabled={(isEdit && !isAccountSet) || isPendingClient}
                          fullWidth
                          name="beneficiary"
                          label="Beneficiary"
                          value={modalData.beneficiary}
                          onChange={handleChange}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ maxLength: 300 }}
                        />
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <SelectSystemCodeWithDescription
                          disabled={(isEdit && !isAccountSet) || isPendingClient}
                          name="currency"
                          label="Currency"
                          type="Currency"
                          value={modalData.currency}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}></div>
                      <div className="grd-cell"></div>
                    </div>
                    <div className="grd-row">
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <div style={{ display: isEntity() ? 'block' : 'none' }}>
                          <SelectTaxCountry
                            disabled={(isEdit && !isAccountSet) || isPendingClient}
                            name="formationCountry"
                            label="Formation Country"
                            value={modalData.formationCountry}
                            key={modalData.formationCountry}
                            onChange={handleChange}
                            style={{
                              display: isEntity() ? 'block' : 'none',
                            }}
                          ></SelectTaxCountry>
                        </div>
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <div style={{ display: isEntity() ? 'block' : 'none' }}>
                          <AutoCompleteState
                            disabled={(isEdit && !isAccountSet) || isPendingClient}
                            name="formationState"
                            label="State"
                            placeholder="State"
                            value={modalData.formationState}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                      <div
                        className="grd-cell"
                        style={{
                          display: isEntity() ? 'block' : 'none',
                        }}
                      >
                        <TextField
                          fullWidth
                          disabled={(isEdit && !isAccountSet) || isPendingClient}
                          name="formationDate"
                          label="Formation Date"
                          type="date"
                          value={modalData.formationDate}
                          onChange={handleChange}
                          InputLabelProps={{ shrink: true }}
                        />
                      </div>
                      <div className="grd-cell"></div>
                    </div>
                  </AccordionDetails>
                </Accordion>
                <Accordion
                  className={classes.accordion}
                  expanded={expanded === 'taxInformation'}
                  onChange={handleExpandAccordion('taxInformation')}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography className={classes.heading}>Tax Information</Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    <div className="grd-row">
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <SelectTaxCountry
                          disabled={(isEdit && !isAccountSet) || isPendingClient}
                          required
                          name="taxCountry"
                          label="Tax Country"
                          value={modalData.taxCountry}
                          key={modalData.taxCountry}
                          onChange={handleChange}
                          style={{
                            display: isEntity() ? 'block' : 'none',
                          }}
                        ></SelectTaxCountry>
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <SelectSystemCode
                          disabled={(isEdit && !isAccountSet) || isPendingClient}
                          name="tefra"
                          label="TEFRA"
                          type="TEFRA"
                          value={modalData.tefra}
                          onChange={handleChange}
                        ></SelectSystemCode>
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <SelectSystemCode
                          disabled={(isEdit && !isAccountSet) || isPendingClient}
                          name="w8w9"
                          label="W8W9"
                          type="W8/W9"
                          value={modalData.w8w9}
                          onChange={handleChange}
                        ></SelectSystemCode>
                      </div>
                    </div>
                    <div className="grd-row">
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <TextField
                          disabled={(isEdit && !isAccountSet) || isPendingClient}
                          fullWidth
                          name="w8w9ReceivedDate"
                          label="W8W9 Received Date"
                          type="date"
                          value={modalData.w8w9ReceivedDate}
                          onChange={handleChange}
                          InputLabelProps={{ shrink: true }}
                        />
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <TextField
                          fullWidth
                          disabled={(isEdit && !isAccountSet) || isPendingClient}
                          name="taxBracket"
                          label="Tax Bracket"
                          type="number"
                          value={modalData.taxBracket}
                          onChange={handleChange}
                          InputLabelProps={{ shrink: true }}
                        />
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}></div>
                      <div
                        className=""
                        style={{
                          marginRight: 20,
                          display: isEntity() ? 'block' : 'none',
                        }}
                      ></div>
                    </div>
                  </AccordionDetails>
                </Accordion>
                <Accordion
                  className={classes.accordion}
                  expanded={expanded === 'trade'}
                  onChange={handleExpandAccordion('trade')}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography className={classes.heading}>Trade</Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    <div className="grd-row">
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <SelectSystemCode
                          disabled={(isEdit && !isAccountSet) || isPendingClient}
                          // required={true}
                          name="marginType"
                          label="Margin Type"
                          type="Margin Type"
                          value={modalData.marginType}
                          onChange={handleChange}
                        ></SelectSystemCode>
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <TextField
                          disabled={(isEdit && !isAccountSet) || isPendingClient}
                          fullWidth
                          name="largeTraderId"
                          label="Large Trader ID"
                          value={modalData.largeTraderId}
                          onChange={handleChange}
                          inputProps={{ maxLength: 50 }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              disabled={(isEdit && !isAccountSet) || isPendingClient}
                              name="dayTrader"
                              checked={modalData.dayTrader}
                              onChange={handleChange}
                            />
                          }
                          label="Day Trader"
                        />
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <TextField
                          disabled={(isEdit && !isAccountSet) || isPendingClient}
                          fullWidth
                          name="multiplier"
                          label="Multiplier"
                          type="number"
                          value={modalData.multiplier}
                          onChange={handleChange}
                          InputLabelProps={{ shrink: true }}
                        />
                      </div>
                    </div>
                    <div className="grd-row">
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              disabled={(isEdit && !isAccountSet) || isPendingClient}
                              name="allowLiquidInvestment"
                              checked={modalData.allowLiquidInvestment}
                              onChange={handleChange}
                            />
                          }
                          label="Allow Liquidation"
                        />
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              disabled={(isEdit && !isAccountSet) || isPendingClient}
                              name="accreditedInvestor"
                              checked={modalData.accreditedInvestor}
                              onChange={handleChange}
                            />
                          }
                          label="Accredited Investor"
                        />
                      </div>

                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              disabled={(isEdit && !isAccountSet) || isPendingClient}
                              name="marginDisclosure"
                              checked={modalData.marginDisclosure}
                              onChange={handleChange}
                            />
                          }
                          label="Margin Disclosure"
                        />
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              disabled={(isEdit && !isAccountSet) || isPendingClient}
                              name="marginAgreement"
                              checked={modalData.marginAgreement}
                              onChange={handleChange}
                            />
                          }
                          label="Margin Agreement"
                        />
                      </div>
                    </div>
                    <div className="grd-row">
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              disabled={(isEdit && !isAccountSet) || isPendingClient}
                              name="fpslParticipant"
                              checked={modalData.fpslParticipant}
                              onChange={handleChange}
                            />
                          }
                          label="FPSL Participant"
                        />
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              disabled={(isEdit && !isAccountSet) || isPendingClient}
                              name="fdidFlag"
                              checked={modalData.fdidFlag}
                              onChange={handleChange}
                            />
                          }
                          label="FDID Flag"
                        />
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <TextField
                          disabled={(isEdit && !isAccountSet) || isPendingClient}
                          fullWidth
                          name="fdid"
                          label="FDID"
                          value={modalData.fdid}
                          onChange={handleChange}
                          inputProps={{ maxLength: 150 }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <TextField
                          disabled={(isEdit && !isAccountSet) || isPendingClient}
                          fullWidth
                          name="fdidEndReason"
                          label="FDID End Reason"
                          value={modalData.fdidEndReason}
                          onChange={handleChange}
                          inputProps={{ maxLength: 150 }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </div>
                    </div>
                    <div className="grd-row">
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        Trading Priviledge
                      </div>
                    </div>
                    <div className="grd-row">
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              disabled={(isEdit && !isAccountSet) || isPendingClient}
                              name="stocks"
                              checked={modalData.stocks}
                              onChange={handleChange}
                            />
                          }
                          label="Equity"
                        />
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              disabled={(isEdit && !isAccountSet) || isPendingClient}
                              name="option"
                              checked={modalData.option}
                              onChange={handleChange}
                            />
                          }
                          label="Option"
                        />
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              disabled={(isEdit && !isAccountSet) || isPendingClient}
                              name="mutualFunds"
                              checked={modalData.mutualFunds}
                              onChange={handleChange}
                            />
                          }
                          label="Mutual Funds"
                        />
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              disabled={(isEdit && !isAccountSet) || isPendingClient}
                              name="fixedIncome"
                              checked={modalData.fixedIncome}
                              onChange={handleChange}
                            />
                          }
                          label="Fixed Income"
                        />
                      </div>
                    </div>
                    <div className="grd-row">
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <TextField
                          fullWidth
                          name="webTermConditions"
                          label="Web Term Conditions"
                          type="datetime-local"
                          value={modalData.webTermConditions}
                          onChange={handleChange}
                          InputLabelProps={{ shrink: true }}
                          disabled={isEdit}
                        />
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <TextField
                          fullWidth
                          name="disclosureStatement"
                          label="Disclosure Statement"
                          type="datetime-local"
                          value={modalData.disclosureStatement}
                          onChange={handleChange}
                          InputLabelProps={{ shrink: true }}
                          disabled={isEdit}
                        />
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <TextField
                          fullWidth
                          name="feeSchedule"
                          label="Fee Schedule"
                          type="datetime-local"
                          value={modalData.feeSchedule}
                          onChange={handleChange}
                          InputLabelProps={{ shrink: true }}
                          disabled={isEdit}
                        />
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}></div>
                    </div>
                    <div className="grd-row">
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <SelectSystemCode
                          disabled={(isEdit && !isAccountSet) || isPendingClient}
                          type="Investment Objective"
                          name="investmentObjectiveCode"
                          label="Investment Objective Code"
                          value={modalData.investmentObjectiveCode}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <TextField
                          disabled={(isEdit && !isAccountSet) || isPendingClient}
                          fullWidth
                          name="investmentObjectiveDate"
                          label="Investment Objective Date"
                          type="date"
                          value={modalData.investmentObjectiveDate}
                          onChange={handleChange}
                          InputLabelProps={{ shrink: true }}
                        />
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <SelectSystemCode
                          disabled={(isEdit && !isAccountSet) || isPendingClient}
                          freeSolo={true}
                          name="annualIncome"
                          label="Annual Income"
                          type="Investment Objectives"
                          subType="Annual Income"
                          itemLabel="code"
                          value={modalData.annualIncome}
                          onChange={handleChange}
                        ></SelectSystemCode>
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <SelectSystemCode
                          disabled={(isEdit && !isAccountSet) || isPendingClient}
                          freeSolo={true}
                          name="liquidNetWorth"
                          label="Liquid Net Worth"
                          type="Investment Objectives"
                          subType="Liquid Net Worth"
                          itemLabel="code"
                          value={modalData.liquidNetWorth}
                          onChange={handleChange}
                        ></SelectSystemCode>
                      </div>
                    </div>
                    <div className="grd-row">
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <SelectSourceOfFunds
                          disabled={(isEdit && !isAccountSet) || isPendingClient}
                          name="sourceOfFunds"
                          label="Source of Funds"
                          value={
                            modalData.sourceOfFunds
                              ? modalData.sourceOfFunds?.split(',')?.map((v) => {
                                  return {
                                    value: v,
                                    label: v,
                                  };
                                })
                              : []
                          }
                          onChange={handleMultipleFundsChange}
                        ></SelectSourceOfFunds>
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <SelectSystemCode
                          disabled={(isEdit && !isAccountSet) || isPendingClient}
                          name="serviceBureau"
                          label="Service Bureau"
                          type="Service Bureau"
                          value={modalData.serviceBureau}
                          onChange={handleChange}
                        ></SelectSystemCode>
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <SelectSystemCode
                          disabled={(isEdit && !isAccountSet) || isPendingClient}
                          name="riskTolerance"
                          label="Risk Profile"
                          type="Risk Tolerance"
                          value={modalData.riskTolerance}
                          onChange={handleChange}
                        ></SelectSystemCode>
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              disabled={(isEdit && !isAccountSet) || isPendingClient}
                              name="cashSweepParticipant"
                              checked={modalData.cashSweepParticipant}
                              onChange={handleChange}
                            />
                          }
                          label="Cash Sweep Participant"
                        />
                      </div>
                    </div>
                    <div className="grd-row">
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <SelectSystemCode
                          disabled={(isEdit && !isAccountSet) || isPendingClient}
                          name="plType"
                          label="PL Type"
                          type="PL Type"
                          value={modalData.plType}
                          onChange={handleChange}
                        ></SelectSystemCode>
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}>
                        <TextField
                          disabled
                          fullWidth
                          name="lastActivityDate"
                          label="Last Activity Date"
                          type="date"
                          value={modalData.lastActivityDate}
                          onChange={handleChange}
                          InputLabelProps={{ shrink: true }}
                        />
                      </div>
                      <div className="grd-cell" style={{ marginRight: 20 }}></div>
                      <div className="grd-cell" style={{ marginRight: 20 }}></div>
                    </div>
                  </AccordionDetails>
                </Accordion>
                <Accordion
                  className={classes.accordion}
                  expanded={expanded === 'other'}
                  onChange={handleExpandAccordion('other')}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography className={classes.heading}>Other</Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    <div className="grd-row">
                      {!isImport ? (
                        <div
                          className="grd-cell"
                          style={{ marginRight: 20, marginTop: 18 }}
                        >
                          <ImageDropZone
                            disabled={(isEdit && !isAccountSet) || isPendingClient}
                            label="Signature"
                            onChangeStatus={handleUploadedPhotoStatus}
                            onSubmit={handleUploadPhoto}
                            initialFiles={[initialFile]}
                          />
                        </div>
                      ) : null}
                      <div className="grd-cell">
                        <div className="grd-row">
                          <div className="grd-cell" style={{ marginRight: 20 }}>
                            <TextField
                              disabled={(isEdit && !isAccountSet) || isPendingClient}
                              fullWidth
                              // required={requiredField.activatedAt}
                              name="activatedAt"
                              label="Activated At"
                              type="datetime-local"
                              value={modalData.activatedAt}
                              onChange={handleChange}
                              InputLabelProps={{ shrink: true }}
                            />
                          </div>
                        </div>
                        <div className="grd-row">
                          <div className="grd-cell" style={{ marginRight: 20 }}>
                            <TextField
                              disabled={(isEdit && !isAccountSet) || isPendingClient}
                              fullWidth
                              name="statusReason"
                              label="Status Reason"
                              value={modalData.statusReason}
                              onChange={handleChange}
                              inputProps={{ maxLength: 150 }}
                              InputLabelProps={{ shrink: true }}
                            />
                          </div>
                        </div>
                        <div className="grd-row">
                          <div className="grd-cell" style={{ marginRight: 30 }}>
                            <FormControl
                              fullWidth
                              disabled={(isEdit && !isAccountSet) || isPendingClient}
                              style={{
                                marginTop: 0,
                                marginBottom: 0,
                              }}
                            >
                              <InputLabel shrink id="kycStatus">
                                KYC Status
                              </InputLabel>
                              <Select
                                name="kycStatus"
                                fullWidth
                                value={modalData.kycStatus}
                                onChange={handleChange}
                              >
                                <MenuItem value="Pending">Pending</MenuItem>
                                <MenuItem value="Approved">Approved</MenuItem>
                                <MenuItem value="Denied">Denied</MenuItem>
                              </Select>
                            </FormControl>
                          </div>
                        </div>
                        <div className="grd-row">
                          <div className="grd-cell" style={{ marginRight: 30 }}>
                            <TextField
                              disabled={(isEdit && !isAccountSet) || isPendingClient}
                              fullWidth
                              name="securityWordHint"
                              label="Security Word Hint"
                              value={modalData.securityWordHint}
                              onChange={handleChange}
                              inputProps={{ maxLength: 50 }}
                              InputLabelProps={{ shrink: true }}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="grd-cell">
                        <div className="grd-row">
                          <div className="grd-cell" style={{ marginRight: 20 }}>
                            <TextField
                              fullWidth
                              disabled={true}
                              name="pendingClosureAt"
                              label="Pending Closure At"
                              type="datetime-local"
                              value={modalData.pendingClosureAt}
                              onChange={handleChange}
                              InputLabelProps={{ shrink: true }}
                            />
                          </div>
                        </div>
                        <div className="grd-row">
                          <div className="grd-cell" style={{ marginRight: 20 }}>
                            {!isImport ? (
                              <div
                                className="grd-cell"
                                style={{ marginRight: 20, marginTop: 24 }}
                              >
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      disabled={
                                        (isEdit && !isAccountSet) || isPendingClient
                                      }
                                      name="signed"
                                      checked={modalData.signed}
                                      onChange={handleChange}
                                    />
                                  }
                                  label="Signed"
                                />
                              </div>
                            ) : (
                              <div className="" style={{ marginRight: 20 }}></div>
                            )}
                          </div>
                        </div>
                        <div className="grd-row">
                          <div className="grd-cell" style={{ marginRight: 20 }}>
                            <TextField
                              fullWidth
                              disabled={(isEdit && !isAccountSet) || isPendingClient}
                              name="promoCode"
                              label="Promo Code"
                              value={modalData.promoCode}
                              onChange={handleChange}
                              inputProps={{ maxLength: 50 }}
                              InputLabelProps={{ shrink: true }}
                            />
                          </div>
                        </div>
                        <div className="grd-row">
                          <div className="grd-cell" style={{ marginRight: 20 }}>
                            <TextField
                              fullWidth
                              name="onboardingPercent"
                              label="Onboarding Percent"
                              value={modalData.onboardingPercent}
                              onChange={handleChange}
                              inputProps={{ maxLength: 150 }}
                              InputLabelProps={{ shrink: true }}
                              disabled={true}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="grd-cell">
                        <div className="grd-row">
                          <div className="grd-cell" style={{ marginRight: 20 }}>
                            <SelectSystemCode
                              disabled={(isEdit && !isAccountSet) || isPendingClient}
                              name="status"
                              label="Status"
                              type="Status"
                              subType="Client Setup"
                              value={modalData.status}
                              onChange={handleChange}
                            ></SelectSystemCode>
                          </div>
                        </div>
                        <div className="grd-row">
                          <div className="grd-cell" style={{ marginRight: 20 }}>
                            <TextField
                              disabled={(isEdit && !isAccountSet) || isPendingClient}
                              fullWidth
                              name="internalStatus"
                              label="Internal Status"
                              value={modalData.internalStatus}
                              onChange={handleChange}
                              inputProps={{ readOnly: true, maxLength: 150 }}
                              InputLabelProps={{ shrink: true }}
                            />
                          </div>
                        </div>
                        <div className="grd-row">
                          <div className="grd-cell" style={{ marginRight: 20 }}>
                            <TextField
                              disabled={(isEdit && !isAccountSet) || isPendingClient}
                              fullWidth
                              name="securityWord"
                              label="Security Word"
                              value={modalData.securityWord}
                              onChange={handleChange}
                              inputProps={{ maxLength: 50 }}
                              InputLabelProps={{ shrink: true }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </AccordionDetails>
                </Accordion>
              </TabPanel>
              <TabPanel className={classes.tabBody} value={tabIndexValue} index={1}>
                <OwnerTable
                  accountId={modalData.accountId}
                  list={ownerList}
                  set={setOwnerList}
                  importMode={isImport}
                  isPendingClient={isPendingClient}
                />
              </TabPanel>
              <TabPanel className={classes.tabBody} value={tabIndexValue} index={2}>
                <Box>
                  <CustomerTypeTable
                    accountId={modalData.accountId}
                    list={customerTypeList}
                    set={setCustomerTypeList}
                  />
                </Box>
              </TabPanel>
              <TabPanel className={classes.tabBody} value={tabIndexValue} index={3}>
                <Box>
                  <SubAccountTable
                    accountId={modalData.accountId}
                    list={subAccountList}
                    set={setSubAccountList}
                  />
                </Box>
              </TabPanel>
              <TabPanel className={classes.tabBody} value={tabIndexValue} index={4}>
                <Box>
                  <NoteTable
                    accountId={modalData.accountId}
                    list={noteList}
                    set={setNoteList}
                    style={{ paddingBottom: 0 }}
                    isPendingClient={isPendingClient}
                  />
                </Box>
              </TabPanel>
              <TabPanel className={classes.tabBody} value={tabIndexValue} index={5}>
                <Box>
                  <AttachmentTable
                    accountId={modalData.accountId}
                    style={{ paddingBottom: 0 }}
                  />
                </Box>
              </TabPanel>
            </div>
          </div>
          <div
            className={classes.modalFooter}
            style={{ margin: 0, padding: '10px 30px 30px 25px' }}
          >
            <div
              className="grd-cell-none"
              style={!isPendingClient ? { marginRight: 10 } : {}}
            >
              <Button
                disabled={loading}
                color="primary"
                label="Close"
                onClick={() => {
                  handleClose();
                }}
              />
            </div>
            {!isPendingClient && (
              <div className="grd-cell-none">
                <Button
                  disabled={loading}
                  loading={loading}
                  color="secondary"
                  label={loading ? 'Saving...' : 'Save'}
                  onClick={() => {
                    handleClose(modalData, isEdit);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </Fade>
    </Modal>
  );
}
